import { useEffect, useState } from "react";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Plus, Edit, Trash2, Shield } from "lucide-react";
import { toast } from "sonner";

export default function Teams() {
  const [teams, setTeams] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", category: "", coach_name: "", description: "" });

  const load = () => api.get("/teams").then(({ data }) => setTeams(data));
  useEffect(() => { load(); }, []);

  const reset = () => {
    setEditing(null);
    setForm({ name: "", category: "", coach_name: "", description: "" });
  };

  const openCreate = () => { reset(); setOpen(true); };
  const openEdit = (t) => {
    setEditing(t);
    setForm({ name: t.name, category: t.category, coach_name: t.coach_name || "", description: t.description || "" });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.patch(`/teams/${editing.id}`, form);
        toast.success("Équipe modifiée");
      } else {
        await api.post("/teams", form);
        toast.success("Équipe créée");
      }
      setOpen(false);
      reset();
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const onDelete = async (id) => {
    if (!confirm("Supprimer cette équipe ?")) return;
    await api.delete(`/teams/${id}`);
    toast.success("Supprimée");
    load();
  };

  return (
    <div>
      <PageHeader
        overline="Effectif"
        title="Équipes"
        description="Organisez vos catégories : U17, U19, Senior, féminines…"
        actions={
          <Button onClick={openCreate} data-testid="create-team-btn" className="btn-club">
            <Plus className="w-4 h-4 mr-2" /> Nouvelle équipe
          </Button>
        }
      />

      {teams.length === 0 ? (
        <EmptyState onAdd={openCreate} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="teams-grid">
          {teams.map((t) => (
            <div key={t.id} className="bg-white border border-[#E0E0E0] rounded-md p-5 hover:border-[#C62828]/40 transition" data-testid={`team-card-${t.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="w-10 h-10 rounded-md bg-[#C62828]/10 text-[#C62828] flex items-center justify-center">
                  <Shield className="w-5 h-5" />
                </div>
                <span className="text-xs tracking-overline text-[#616161] bg-[#F4F5F7] px-2 py-1 rounded">{t.category}</span>
              </div>
              <h3 className="font-display text-lg font-bold">{t.name}</h3>
              {t.coach_name && <p className="text-sm text-[#616161] mt-1">Coach : {t.coach_name}</p>}
              {t.description && <p className="text-sm text-[#616161] mt-2 line-clamp-2">{t.description}</p>}
              <div className="mt-4 flex items-center justify-between pt-3 border-t border-[#E0E0E0]">
                <p className="text-xs text-[#616161]">{t.player_count} joueur{t.player_count > 1 ? "s" : ""}</p>
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" onClick={() => openEdit(t)} data-testid={`edit-team-${t.id}`}>
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => onDelete(t.id)} data-testid={`delete-team-${t.id}`}>
                    <Trash2 className="w-4 h-4 text-[#C62828]" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="team-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? "Modifier l'équipe" : "Nouvelle équipe"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label>Nom *</Label>
              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="team-name-input" />
            </div>
            <div>
              <Label>Catégorie *</Label>
              <Input required value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                placeholder="U17, U19, Senior..." data-testid="team-category-input" />
            </div>
            <div>
              <Label>Entraîneur</Label>
              <Input value={form.coach_name} onChange={(e) => setForm({ ...form, coach_name: e.target.value })} data-testid="team-coach-input" />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="team-description-input" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
              <Button type="submit" className="btn-club" data-testid="team-submit-btn">
                {editing ? "Enregistrer" : "Créer"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function EmptyState({ onAdd }) {
  return (
    <div className="bg-white border border-dashed border-[#E0E0E0] rounded-md p-12 text-center">
      <Shield className="w-10 h-10 text-[#C62828]/40 mx-auto mb-4" />
      <h3 className="font-display text-xl font-bold">Aucune équipe</h3>
      <p className="text-sm text-[#616161] mt-2 mb-5">Créez votre première catégorie pour organiser vos joueurs.</p>
      <Button onClick={onAdd} className="btn-club">
        <Plus className="w-4 h-4 mr-2" /> Créer une équipe
      </Button>
    </div>
  );
}
